import React from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { ListContent } from '@components/Feed/ListViews/components/ListContent';
import { ListMinimalUser } from '@components/Feed/ListViews/components/ListMinimalUser';
import { ToggledAvatar } from '@components/Feed/ListViews/components/ToggledAvatar';
import { ModalButton } from '@components/Feed/ListViews/components/TotalOtherText';
import {
  FeedListViewContainer,
  FeedListViewSubText,
  FeedListViewWrapper,
} from '@components/Feed/ListViews/components/shared';
import { PlaylistReleasesModal } from '@components/modals/PlaylistReleases';
import { useMediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, ReleasesAddedRemovedListFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { RELEASE_PLAYLIST_ACTION_TYPE, ReleasesPlaylistList } from './ReleasesPlaylist';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleasesAddedRemovedList on ReleasesAddedRemovedFromPlaylist {
    id
    date
    numAddedReleases
    numRemovedReleases
    user {
      id
      avatar {
        id
        url
      }
      username
      publicAddress
      webappUri
      ...MinimalUser
    }
    playlist {
      id
      ...PlaylistActivityInfo
    }
    addedReleases(pagination: { first: 3 }) {
      edges {
        node {
          id
          ...ReleasePill
        }
      }
    }
  }
`);

export const ReleasesAddedRemovedList = ({
  activityFrag,
  ...props
}: {
  activityFrag: FragmentType<ReleasesAddedRemovedListFragmentDoc>;
  playlistFeed?: boolean;
} & (
  | {
      playlistFeed?: boolean;
    }
  | {
      releaseFeed?: boolean;
    }
)) => {
  const { openModal } = ModalContext.useContainer();

  const playlistFeed = 'playlistFeed' in props ? props.playlistFeed : undefined;
  const releaseFeed = 'releaseFeed' in props ? props.releaseFeed : undefined;
  const forceMobileView = !!playlistFeed;
  const { tablet } = useMediaQuery();

  const {
    id: actionId,
    date,
    playlist,
    numAddedReleases,
    numRemovedReleases,
    addedReleases,
    user: userData,
  } = getFragment(ReleasesAddedRemovedListFragmentDoc, activityFrag);

  const { avatar, username, publicAddress, webappUri } = userData;

  const user = (
    <FeedListViewWrapper
      margin="first"
      className={clsx(forceMobileView && !!playlistFeed && '[&_h3]:text-base-s')}
    >
      <ListMinimalUser userFrag={userData} forceMobileView={forceMobileView} />
    </FeedListViewWrapper>
  );

  if (!playlistFeed && playlist && numAddedReleases > 0) {
    return (
      <ReleasesPlaylistList
        actionId={actionId}
        userFrag={userData}
        playlistFrag={playlist}
        featuredReleases={addedReleases.edges.map(v => v.node)}
        isoDateString={date}
        releaseCount={numAddedReleases}
        actionType={RELEASE_PLAYLIST_ACTION_TYPE.RELEASE_ADDED}
        releaseFeed={releaseFeed}
      />
    );
  }

  return (
    <FeedListViewContainer>
      <ToggledAvatar
        src={avatar?.url}
        username={username}
        walletAddress={publicAddress}
        userWebappUri={webappUri}
        forceMobileView={forceMobileView && tablet}
      />
      <ListContent isoDateString={date} truncateChildren>
        {user}

        <FeedListViewSubText forceMobileView={forceMobileView}>added</FeedListViewSubText>
        {SPACE}
        {playlist && (
          <>
            <ModalButton
              onClick={() => {
                openModal(ModalType.PLAYLIST_ACTIONS, {
                  body: (
                    <PlaylistReleasesModal
                      playlistId={playlist.id}
                      actionId={actionId}
                      type="added"
                    />
                  ),
                });
              }}
              forceMobileView={forceMobileView}
            >
              {numAddedReleases}
              {SPACE}
              {getSingularOrPlural({
                word: 'Sound',
                count: numAddedReleases,
              })}
              {SPACE}
            </ModalButton>

            <FeedListViewSubText forceMobileView={forceMobileView}>and removed</FeedListViewSubText>
            {SPACE}
            <ModalButton
              onClick={() => {
                openModal(ModalType.PLAYLIST_ACTIONS, {
                  body: (
                    <PlaylistReleasesModal
                      playlistId={playlist.id}
                      actionId={actionId}
                      type="removed"
                    />
                  ),
                });
              }}
              forceMobileView={forceMobileView}
            >
              {numRemovedReleases}
              {SPACE}
              {getSingularOrPlural({
                word: 'Sound',
                count: numRemovedReleases,
              })}
            </ModalButton>
          </>
        )}
      </ListContent>
    </FeedListViewContainer>
  );
};
